import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Helmet } from '@/components/Helmet';

export default function Success() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { checking } = useRequireMfa();
  const [searchParams] = useSearchParams();
  const [updating, setUpdating] = useState(true);
  
  const domainId = searchParams.get('domain_id');
  const subscriptionId = searchParams.get('subscription_id');
  const bulkIds = searchParams.get('bulk');
  const flow = searchParams.get('flow');
  const domainIds = [
    ...(domainId ? [domainId] : []),
    ...(bulkIds ? bulkIds.split(',').map((id) => id.trim()).filter(Boolean) : []),
  ];

  useEffect(() => {
    const updateDomainStatus = async () => {
      if (!user) return;
      
      try {
        const updateData: Record<string, string | null> = { 
          stripe_subscription_id: subscriptionId || null,
        };
        
        // Update single domain
        if (domainId) {
          const { error } = await supabase
            .from('domains')
            .update(updateData)
            .eq('id', domainId)
            .eq('user_id', user.id);
          
          if (error) {
            console.error('Failed to update domain:', error);
            toast.error('Failed to update domain record');
          }
        }
        
        // Update bulk domains if any
        if (bulkIds) {
          const ids = bulkIds.split(',');
          for (const id of ids) {
            const { error } = await supabase
              .from('domains')
              .update(updateData)
              .eq('id', id)
              .eq('user_id', user.id);
            
            if (error) {
              console.error('Failed to update bulk domain:', id, error);
            }
          }
        }
        
        toast.success('Payment confirmed. Domain setup is pending.');
      } catch (err) {
        console.error('Error updating domains:', err);
      } finally {
        setUpdating(false);
      }
    };

    if (user && (domainId || bulkIds)) {
      updateDomainStatus();
    } else if (user) {
      setUpdating(false);
    }
  }, [user, loading, navigate, domainId, subscriptionId, bulkIds]);

  useEffect(() => {
    if (updating) return;
    const canNotify =
      typeof window !== 'undefined' &&
      window.opener &&
      !window.opener.closed &&
      window.location.origin;
    if (flow === 'add-domain') {
      if (canNotify) {
        window.opener.postMessage(
          { type: 'checkout_complete', domainId: domainId || null, bulkIds: bulkIds ? bulkIds.split(',') : [] },
          window.location.origin,
        );
        localStorage.setItem(
          'add-domain-secure',
          JSON.stringify({ domainIds, ts: Date.now() }),
        );
        window.close();
        return;
      }
      navigate(`/portal/add-domain?step=secure${domainId ? `&domain_id=${domainId}` : ''}${bulkIds ? `&bulk=${bulkIds}` : ''}`);
      return;
    }
    if (canNotify) {
      window.opener.postMessage(
        { type: 'checkout_complete', domainId: domainId || null, bulkIds: bulkIds ? bulkIds.split(',') : [] },
        window.location.origin,
      );
      window.close();
      return;
    }
    if (flow === 'resume') {
      navigate('/portal/billing');
    }
  }, [bulkIds, domainId, domainIds, flow, navigate, updating]);

  if (checking || loading || !user || updating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Finalizing Checkout | ShadowScan Portal</title>
          <meta
            name="description"
            content="Finishing your checkout and preparing the next setup steps for your ShadowScan domain."
          />
      </Helmet>
        <div className="flex items-center gap-2 text-primary">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Finalizing your checkout...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Helmet>
        <title>Checkout Complete | ShadowScan Portal</title>
        <meta
          name="description"
          content="Checkout completed. Return to the portal to continue securing your sites."
        />
      </Helmet>
      <div className="flex flex-col items-center gap-3 text-center max-w-md">
        <div className="text-lg font-semibold">Checkout complete</div>
        <p className="text-sm text-muted-foreground">
          You can close this tab and continue setup in your portal.
        </p>
        <Button onClick={() => navigate(flow === 'add-domain' ? '/portal/add-domain?step=secure' : '/portal/billing')}>
          Return to portal
        </Button>
      </div>
    </div>
  );
}
